import React, { useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import { Dialog, IconButton } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import { useTranslation } from 'react-i18next'
import { NewLayout } from '../components/newLayout/NewLayout'
import { tokens } from '../styles/tokens'
import { MARKET_VIDEOS, MarketVideo } from '../constants/marketVideos'

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(3),
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(2),
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    background: tokens.color.white,
    borderRadius: tokens.radius.lg,
    boxShadow: tokens.shadow.sm,
    overflow: 'hidden',
    cursor: 'pointer',
    border: `1px solid ${tokens.color.neutral[100]}`,
    transition: `transform ${tokens.transition.fast}, box-shadow ${tokens.transition.fast}`,
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: tokens.shadow.md,
    },
    '&:hover $playBadge': {
      opacity: 1,
      transform: 'translate(-50%, -50%) scale(1)',
    },
  },
  thumb: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    background: tokens.color.neutral[900],
    overflow: 'hidden',
  },
  thumbVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none',
  },
  playBadge: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) scale(0.9)',
    width: '56px',
    height: '56px',
    borderRadius: tokens.radius.full,
    background: 'rgba(17, 69, 127, 0.82)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.color.white,
    opacity: 0.85,
    transition: `opacity ${tokens.transition.fast}, transform ${tokens.transition.fast}`,
    boxShadow: tokens.shadow.md,
  },
  playIcon: {
    fontSize: '2rem',
  },
  meta: {
    padding: theme.spacing(1.75, 2, 2),
  },
  title: {
    margin: 0,
    fontSize: '0.95rem',
    fontWeight: tokens.font.weight.semibold,
    color: tokens.color.neutral[900],
  },
  subtitle: {
    margin: 0,
    marginTop: theme.spacing(0.5),
    fontSize: '0.8rem',
    color: tokens.color.neutral[500],
  },
  empty: {
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    color: tokens.color.neutral[500],
    fontSize: '0.95rem',
  },
  dialogPaper: {
    background: '#000',
    overflow: 'hidden',
    borderRadius: tokens.radius.lg,
  },
  playerWrapper: {
    position: 'relative',
    width: '100%',
    background: '#000',
  },
  player: {
    width: '100%',
    maxHeight: '80vh',
    display: 'block',
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    color: tokens.color.white,
    background: 'rgba(0, 0, 0, 0.45)',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.7)',
    },
  },
}))

export const MarketOverview: React.FC = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [selected, setSelected] = useState<MarketVideo | null>(null)

  return (
    <NewLayout pageName="MARKET_OVERVIEW">
      {MARKET_VIDEOS.length === 0 ? (
        <p className={classes.empty}>{t('MARKET_OVERVIEW_EMPTY')}</p>
      ) : (
        <div className={classes.grid}>
          {MARKET_VIDEOS.map(video => (
            <div
              key={video.id}
              className={classes.card}
              onClick={() => setSelected(video)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelected(video)
                }
              }}
            >
              <div className={classes.thumb}>
                <video
                  className={classes.thumbVideo}
                  src={`${video.src}#t=3`}
                  preload="metadata"
                  muted
                  playsInline
                />
                <span className={classes.playBadge}>
                  <PlayArrowRoundedIcon className={classes.playIcon} />
                </span>
              </div>
              <div className={classes.meta}>
                <p className={classes.title}>{video.title}</p>
                {video.subtitle && <p className={classes.subtitle}>{video.subtitle}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="lg"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        {selected && (
          <div className={classes.playerWrapper}>
            <IconButton
              className={classes.closeButton}
              aria-label={t('CLOSE')}
              onClick={() => setSelected(null)}
              size="small"
            >
              <CloseRoundedIcon />
            </IconButton>
            <video
              className={classes.player}
              src={selected.src}
              controls
              autoPlay
              controlsList="nodownload"
            />
          </div>
        )}
      </Dialog>
    </NewLayout>
  )
}
